import { AlertTriangle, Check, Download, FileSpreadsheet, Trash2, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import type { FormatoDaPlanilha } from '@obraplan/core/domain/takeoff/mapear-colunas';
import type { FuncaoCandidata } from '@obraplan/core/domain/workforce/job-role-match';
import { asId, type ProjectId, type UnitTypeId } from '@obraplan/core/domain/shared/ids';
import { describeSheet, parseServiceSheet } from '@obraplan/core/domain/takeoff/service-sheet';
import {
  ehPlanilhaPlana,
  lerPlanilhaDeIndices,
  mesclarEstacas,
  mesclarNoCatalogo,
  ordenarAvisos,
  type AbaDaPlanilha,
  type AvisoDaPlanilha,
  type DestinoDeEstacas,
  type LeituraDaPlanilha,
  type MesclaDaPlanilha,
  type MesclaDasEstacas,
  semServicosDesativados,
  type ServicoDesativado,
  type ServicoDisponivel,
  type TipoDeAba,
} from '@obraplan/core/domain/takeoff/planilha-de-indices';
import {
  ehPlanilhaPorPeca,
  juntarQuantidadesPorPeca,
  lerPlanilhaPorPeca,
  mesclarPorPeca,
  type LeituraPorPeca,
  type MesclaPorPeca,
} from '@obraplan/core/domain/takeoff/planilha-por-peca';
import {
  buildCatalog,
  classifySheets,
  conferSheets,
  removidosPelaTroca,
  SHEET_SCOPE_LABEL,
  summarizeCatalog,
  type ClassifiedSheet,
  type RemovidosDaTroca,
  type ServiceCatalog,
  type SheetConference,
  type SheetScope,
} from '@obraplan/core/domain/takeoff/service-catalog';
import { downloadXlsx, toXlsx } from '@/features/reports/xlsx';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import {
  destinosDeEstacas,
  modeloDeIndices,
  nomeDoArquivoDoModelo,
  type ObraDeEstacas,
} from './modelo-de-indices';
import { CompletarParaOrcar } from './CompletarParaOrcar';
import {
  ligacoesDaMaoDeObra,
  passoParaOrcar,
  pedidoParaOrcar,
  produtosDaImportacao,
  SEM_ESCOLHAS,
  type EscolhasParaOrcar,
} from './completar-para-orcar';
import {
  destinoPeloAtrelamento,
  destinosPorPeca,
  type DestinoDaPlanilha,
  type DestinoPorPeca,
  type PavimentoDaObra,
} from './destino-por-peca';
import { lerAbasDoArquivo } from './ler-planilha';
import { MapearColunas } from './MapearColunas';
import { MENSAGEM_DE_ARQUIVO_ILEGIVEL } from './abas-do-arquivo';
import type { WorkspaceAction } from './typology-draft';
import { useCompletarParaOrcar } from './use-completar-para-orcar';

/**
 * A importação da planilha de serviços.
 *
 * A planilha da construtora tem uma aba por tipologia, e a tela precisa dizer, antes de
 * gravar qualquer coisa, **o que ela entendeu de cada uma**: quais são parcela do andar
 * tipo, qual é a soma que não pode entrar duas vezes e qual tem escopo próprio. Depois
 * disso vem a pergunta que decide se a importação vale: as parcelas somam a soma?
 *
 * Nada é gravado até a confirmação, e o que se grava é catálogo — disciplina, serviço e
 * item —, não dinheiro. Preço não é desta fase (RN-173).
 *
 * O mesmo botão aceita a outra planilha, a **plana** de índices, taxas de aço, tipos de
 * estaca e estacas (Fase 27, ADR-177): o sistema exporta o modelo, o usuário preenche e importa
 * de volta. Quem decide qual das duas é o arquivo, pelo cabeçalho (RN-374) — pedir que o usuário
 * escolhesse o formato seria perguntar o que a planilha já responde. O caminho numerado
 * continua igual.
 *
 * A confirmação da plana despacha até três ações: o catálogo (`IMPORT_CATALOG`), os tipos de
 * estaca (`SET_PILE_TYPES`) e as estacas de cada pavimento (`IMPORT_PILES`) — só as que mudam
 * alguma coisa. As estacas chegam "a posicionar" (D5); o pavimento delas é a escolha do
 * "Pavimento das estacas", e a coluna Pavimento da planilha vence essa escolha (F10).
 *
 * A terceira forma é a planilha **por peça** (Fase 28): uma linha por peça × serviço × material,
 * com a quantidade pronta ("B24 leva 33,18 kg de CA 50 - 8 MM"). O cabeçalho a reconhece antes
 * da plana, porque as duas têm Disciplina e Serviço. A confirmação despacha o catálogo com os
 * itens que ela criou (`IMPORT_CATALOG`) e as quantidades da tipologia escolhida
 * (`IMPORT_PIECE_QUANTITIES`), que se juntam às que ela já tinha (RN-386).
 *
 * A prévia por peça também pergunta, uma vez por produto, o que falta para orçar (Fase 29,
 * 13/10/2026 — `CompletarParaOrcar`): o fator do aço, conferido; o da unidade diferente; o
 * provisório do material sem código; e a função da mão de obra que não casou pelo nome. A função
 * escolhida refaz a mescla — a prévia mostra o catálogo que vai entrar —, e o fator e o
 * provisório são gravados no servidor depois de a importação ser despachada.
 */

const SELECT_CLASS =
  'h-11 min-w-0 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

const TOM_POR_ESCOPO: Readonly<Record<SheetScope, 'ok' | 'info' | 'warn' | 'neutral'>> = {
  PARCELA: 'ok',
  SOMA: 'info',
  PROPRIO: 'warn',
  VAZIA: 'neutral',
};

interface PreviaNumerada {
  readonly formato: 'NUMERADA';
  readonly fileName: string;
  readonly sheets: readonly ClassifiedSheet[];
  readonly conference: SheetConference;
  readonly include: ReadonlySet<string>;
}

/**
 * A planilha plana, já mesclada no catálogo **sem gravar nada**: a prévia mostra o que a
 * confirmação vai despachar, e é o mesmo objeto que ela despacha — o que se vê é o que entra.
 */
interface PreviaPlana {
  readonly formato: 'PLANA';
  readonly fileName: string;
  readonly leitura: LeituraDaPlanilha;
  readonly mescla: MesclaDaPlanilha;
}

/**
 * A planilha por peça, lida e casada com o catálogo **sem gravar nada** (Fase 28) — como a
 * plana: a prévia mostra o que a confirmação despacha, e é o mesmo objeto.
 */
interface PreviaPorPeca {
  readonly formato: 'POR_PECA';
  readonly fileName: string;
  readonly leitura: LeituraPorPeca;
  readonly mescla: MesclaPorPeca;
}

type Previa = PreviaNumerada | PreviaPlana | PreviaPorPeca;

/** O mesmo array a cada renderização: o default `[]` seria um array novo por quadro. */
const SEM_SERVICOS: readonly ServicoDisponivel[] = [];
const SEM_DESTINOS: readonly DestinoDeEstacas[] = [];
const SEM_DESATIVADOS: readonly ServicoDesativado[] = [];
const SEM_TIPOS: readonly never[] = [];
const SEM_DESTINOS_POR_PECA: readonly DestinoPorPeca[] = [];
const SEM_DESENHOS: ReadonlyMap<string, string> = new Map();
const SEM_PAVIMENTOS: readonly PavimentoDaObra[] = [];

function numero(value: number, casas = 2): string {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

export function ServiceSheetImport({
  catalog,
  dispatch,
  disponiveis = SEM_SERVICOS,
  desativados = SEM_DESATIVADOS,
  workspace,
  projectId,
  nomesDosDesenhos,
  pavimentosDaObra,
  funcoesDoServico,
}: {
  catalog: ServiceCatalog | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  /**
   * O cadastro de serviços da obra — o que a planilha plana casa e o modelo percorre.
   *
   * Opcional porque o catálogo já guarda os serviços que a tela abriu; o cadastro acrescenta os
   * que ninguém abriu ainda, e sem ele a obra nova (catálogo vazio) não teria onde casar nada.
   */
  disponiveis?: readonly ServicoDisponivel[];
  /**
   * Os serviços que a obra desativou. O catálogo do rascunho ainda os guarda, e a planilha não
   * os usa: o modelo não os lista, o import avisa em vez de criar item neles, e o resumo não os
   * conta.
   */
  desativados?: readonly ServicoDesativado[];
  /**
   * As tipologias e os tipos de estaca da obra — o que as abas Tipos e Estacas casam, e o que o
   * modelo exporta nelas. Sem ele (e sem `projectId`) a tela não lê tipos nem estacas.
   */
  workspace?: ObraDeEstacas;
  /** A obra, para os tipos de estaca que a planilha cria. */
  projectId?: ProjectId;
  /**
   * O nome de cada desenho, pelo id (Fase 28): a coluna ANDAR da planilha por peça diz o
   * desenho que recebe as peças pelo nome dele.
   */
  nomesDosDesenhos?: ReadonlyMap<string, string>;
  /**
   * Todos os pavimentos da estrutura, atrelados ou não a um desenho (03/11/2026): é o
   * atrelamento de Pavimentos e desenhos que diz quantas vezes a área conta, e o pavimento sem
   * desenho que a coluna ANDAR cita trava a importação.
   */
  pavimentosDaObra?: readonly PavimentoDaObra[];
  /**
   * As funções permitidas em cada serviço — a planilha por peça liga a mão de obra à função
   * cadastrada pelo nome (Fase 29), e é ela que dá o R$/h da folha.
   */
  funcoesDoServico?: (serviceKey: string) => readonly FuncaoCandidata[];
}) {
  const [aberto, setAberto] = useState(false);
  const [confirmandoRemocao, setConfirmandoRemocao] = useState(false);
  // O que a tela conta é o que a obra usa: o serviço desativado fica de fora do resumo.
  const resumo =
    catalog === undefined
      ? undefined
      : summarizeCatalog(semServicosDesativados(catalog, desativados));
  const itensDoCatalogo = catalog === undefined ? 0 : summarizeCatalog(catalog).items;
  const temServicos =
    disponiveis.length > 0 ||
    (catalog?.disciplines.some((disciplina) => disciplina.services.length > 0) ?? false);
  // A remoção do catálogo leva as peças importadas junto (Fase 28): a confirmação diz isso.
  const temPecasImportadas =
    workspace?.typologies.some(
      (tipologia) =>
        Object.keys(tipologia.pieceQuantities?.itens ?? {}).length +
          Object.keys(tipologia.pieceQuantities?.servicos ?? {}).length >
        0,
    ) ?? false;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Planilha de serviços</CardTitle>
        <CardDescription>
          A pasta do orçamento, com uma aba por tipologia. Entra o catálogo — disciplina, serviço e
          item —, não o preço: esta fase levanta quantidade (RN-173). A planilha de índices, taxas
          de aço, tipos de estaca e estacas entra pelo mesmo botão: baixe o modelo, preencha e
          importe de volta. E a planilha por peça também — uma linha por peça, serviço e material,
          com a quantidade pronta, que entra na área única da Fundação ou da Contenção, ou na área
          comum de um pavimento.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Dialog open={aberto} onOpenChange={setAberto}>
            {/*
              O rótulo é sempre o mesmo. "Trocar planilha" com o catálogo cheio foi lido como "o
              botão de importar sumiu" (09/10/2026) — e a planilha por peça e a de índices não
              trocam nada, acrescentam. O que cada uma faz com o que já existe é dito dentro do
              diálogo, quando o arquivo já disse qual das três ele é.
            */}
            <DialogTrigger asChild>
              <Button variant="secondary">
                <Upload className="size-4" />
                Importar planilha
              </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[90vh] w-[min(64rem,92vw)] max-w-none flex-col overflow-hidden sm:w-[min(64rem,92vw)]">
              <ImportacaoEmCurso
                anterior={catalog}
                disponiveis={disponiveis}
                desativados={desativados}
                workspace={workspace}
                projectId={projectId}
                nomesDosDesenhos={nomesDosDesenhos}
                pavimentosDaObra={pavimentosDaObra}
                funcoesDoServico={funcoesDoServico}
                onConfirmar={(acoes) => {
                  for (const acao of acoes) dispatch(acao);
                  setAberto(false);
                }}
              />
            </DialogContent>
          </Dialog>

          {/*
            O modelo sai do catálogo de hoje: uma linha por item que a obra já tem, com o índice
            e a perda que já existem, e uma linha vazia por serviço sem item (F11).
          */}
          <Button
            variant="secondary"
            disabled={!temServicos}
            onClick={() =>
              downloadXlsx(
                nomeDoArquivoDoModelo(new Date()),
                toXlsx(modeloDeIndices(catalog, disponiveis, workspace, desativados)),
              )
            }
          >
            <Download className="size-4" />
            Baixar modelo de índices
          </Button>

          {catalog !== undefined && (
            <Button variant="ghost" onClick={() => setConfirmandoRemocao(true)}>
              <Trash2 className="size-4" /> Remover planilha e itens
            </Button>
          )}
        </div>

        <ConfirmDialog
          open={confirmandoRemocao}
          onOpenChange={setConfirmandoRemocao}
          destructive
          title="Remover a planilha e os itens?"
          description={`${
            itensDoCatalogo === 0
              ? 'O catálogo não tem item nenhum, e a planilha sai da obra.'
              : `Apaga ${quantos(itensDoCatalogo, 'item', 'itens')} da obra, com os índices de consumo e as perdas deles — e a planilha importada.`
          }${
            temPecasImportadas
              ? ' As quantidades da planilha por peça saem junto: apontavam para esses itens.'
              : ''
          } As taxas de aço e os tipos de estaca ficam como estão. Não há como desfazer.`}
          confirmLabel={
            itensDoCatalogo === 0
              ? 'Remover planilha'
              : `Remover planilha e ${quantos(itensDoCatalogo, 'item', 'itens')}`
          }
          onConfirm={() => {
            setConfirmandoRemocao(false);
            dispatch({ type: 'CLEAR_CATALOG' });
          }}
        />

        {catalog === undefined || resumo === undefined ? (
          <p className="text-sm text-(--color-content-muted)">
            Nenhuma planilha importada. Ela é um <strong className="font-medium">atalho</strong>, e
            não condição: o cadastro de disciplina, serviço e item também se monta na mão, em Itens
            e consolidação.
          </p>
        ) : (
          <div className="flex flex-col gap-1 text-sm">
            <p className="flex flex-wrap items-center gap-2">
              <FileSpreadsheet className="size-4 text-(--color-content-muted)" aria-hidden="true" />
              <strong className="font-medium">
                {catalog.fileName ?? 'Cadastro montado na tela'}
              </strong>
              <span className="text-(--color-content-muted)">{describeSheet(resumo)}</span>
            </p>
            {catalog.importedAt !== undefined && (
              <p className="text-xs text-(--color-content-muted)">
                {catalog.sources.filter((fonte) => fonte.imported).length} de{' '}
                {catalog.sources.length} abas importadas ·{' '}
                {new Date(catalog.importedAt).toLocaleString('pt-BR')}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── O diálogo ───────────────────────────────────────────────────────────────────

function ImportacaoEmCurso({
  anterior,
  disponiveis,
  desativados,
  workspace,
  projectId,
  nomesDosDesenhos,
  pavimentosDaObra,
  funcoesDoServico,
  onConfirmar,
}: {
  anterior: ServiceCatalog | undefined;
  disponiveis: readonly ServicoDisponivel[];
  desativados: readonly ServicoDesativado[];
  workspace: ObraDeEstacas | undefined;
  projectId: ProjectId | undefined;
  nomesDosDesenhos: ReadonlyMap<string, string> | undefined;
  pavimentosDaObra: readonly PavimentoDaObra[] | undefined;
  funcoesDoServico: ((serviceKey: string) => readonly FuncaoCandidata[]) | undefined;
  onConfirmar: (acoes: readonly WorkspaceAction[]) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [previa, setPrevia] = useState<Previa | undefined>(undefined);
  const [erro, setErro] = useState<string | undefined>(undefined);
  const [lendo, setLendo] = useState(false);
  const [destinoEscolhido, setDestinoEscolhido] = useState<UnitTypeId | undefined>(undefined);
  /** A tipologia que recebe a planilha por peça, quando a pessoa trocou a sugerida. */
  const [destinoEscolhidoDasPecas, setDestinoEscolhidoDasPecas] = useState<UnitTypeId | undefined>(
    undefined,
  );
  /** A troca numerada que apaga itens, esperando o "sim" da pessoa. */
  const [trocaPendente, setTrocaPendente] = useState<
    { readonly catalogo: ServiceCatalog; readonly removidos: RemovidosDaTroca } | undefined
  >(undefined);
  /** O que alguém respondeu no "Completar para orçar" da planilha por peça. */
  const [escolhas, setEscolhas] = useState<EscolhasParaOrcar>(SEM_ESCOLHAS);
  /** A planilha lida cujos títulos nenhum formato reconheceu: espera o mapeamento manual. */
  const [naoReconhecida, setNaoReconhecida] = useState<
    { readonly fileName: string; readonly abas: readonly AbaDaPlanilha[] } | undefined
  >(undefined);

  /**
   * As abas lidas viradas em prévia. O cabeçalho decide o formato (RN-374), ou o mapeamento
   * manual o diz (`formato`). A por peça vem antes da plana: as duas têm Disciplina e Serviço,
   * e só a por peça tem Nome e Quantidade (Fase 28). As duas são mescladas já aqui, sem gravar:
   * a prévia mostra o que a confirmação vai despachar.
   *
   * Devolve se montou a prévia. Não monta quando nenhum formato reconhece os títulos — e aí
   * quem chama abre o mapeamento manual (22/10/2026), em vez de parar num aviso.
   */
  function processar(
    fileName: string,
    lidas: readonly AbaDaPlanilha[],
    formato?: FormatoDaPlanilha,
  ): boolean {
    if (formato === 'POR_PECA' || (formato === undefined && ehPlanilhaPorPeca(lidas))) {
      const leitura = lerPlanilhaPorPeca(lidas);
      setDestinoEscolhidoDasPecas(undefined);
      setPrevia({
        formato: 'POR_PECA',
        fileName,
        leitura,
        mescla: mesclarPorPeca(anterior, disponiveis, leitura, desativados, funcoesDoServico),
      });
      return true;
    }
    if (formato === 'PLANA' || (formato === undefined && ehPlanilhaPlana(lidas))) {
      const leitura = lerPlanilhaDeIndices(lidas);
      setPrevia({
        formato: 'PLANA',
        fileName,
        leitura,
        mescla: mesclarNoCatalogo(anterior, disponiveis, leitura, desativados),
      });
      return true;
    }

    const abas = classifySheets(lidas.map((aba) => parseServiceSheet(aba.name, aba.rows)));
    if (abas.every((aba) => aba.scope === 'VAZIA')) return false;
    setPrevia({
      formato: 'NUMERADA',
      fileName,
      sheets: abas,
      conference: conferSheets(abas),
      // A soma e as vazias ficam de fora por padrão; o resto entra. É a escolha que
      // acerta na planilha do cliente sem impedir ninguém de discordar.
      include: new Set(
        abas
          .filter((aba) => aba.scope === 'PARCELA' || aba.scope === 'PROPRIO')
          .map((aba) => aba.sheet.name),
      ),
    });
    return true;
  }

  async function escolher(file: File): Promise<void> {
    setLendo(true);
    setErro(undefined);
    setNaoReconhecida(undefined);
    // O arquivo novo é outra importação: as respostas da anterior eram de outros produtos.
    setEscolhas(SEM_ESCOLHAS);
    try {
      // A leitura roda num worker com prazo, e sempre termina: nas abas, ou na frase que diz
      // por que o arquivo não abriu (22/10/2026 — antes, a planilha estranha congelava aqui).
      const leitura = await lerAbasDoArquivo(file);
      if (!leitura.ok) {
        setPrevia(undefined);
        setErro(leitura.mensagem);
        return;
      }
      if (!processar(file.name, leitura.abas)) {
        setPrevia(undefined);
        setNaoReconhecida({ fileName: file.name, abas: leitura.abas });
      }
    } catch (causa) {
      console.error('[obraplan] falha ao ler a planilha de serviços', file.name, causa);
      setPrevia(undefined);
      setErro(MENSAGEM_DE_ARQUIVO_ILEGIVEL);
    } finally {
      setLendo(false);
    }
  }

  /**
   * A planilha com as colunas que a pessoa escolheu, pelo mesmo caminho da reconhecida. Se nem
   * assim sai prévia (a numerada sem numeração na coluna Item), o mapeamento volta com a planilha
   * **original** e a escolha que a pessoa fez, e o aviso diz o que conferir.
   */
  function lerMapeada(
    original: { readonly fileName: string; readonly abas: readonly AbaDaPlanilha[] },
    formato: FormatoDaPlanilha,
    mapeadas: readonly AbaDaPlanilha[],
  ): void {
    setErro(undefined);
    setNaoReconhecida(undefined);
    try {
      if (!processar(original.fileName, mapeadas, formato)) {
        setPrevia(undefined);
        setErro(
          'Nenhuma linha da coluna escolhida como Item tem a numeração (3, 3.1, 3.1.1), e é ela que monta disciplina, serviço e item na numerada. Confira a coluna, ou escolha outro formato.',
        );
        setNaoReconhecida(original);
      }
    } catch (causa) {
      console.error('[obraplan] falha ao ler a planilha mapeada', original.fileName, causa);
      setPrevia(undefined);
      setErro(MENSAGEM_DE_ARQUIVO_ILEGIVEL);
      setNaoReconhecida(original);
    }
  }

  function alternar(name: string): void {
    setPrevia((atual) => {
      if (atual?.formato !== 'NUMERADA') return atual;
      const proximo = new Set(atual.include);
      if (proximo.has(name)) proximo.delete(name);
      else proximo.add(name);
      return { ...atual, include: proximo };
    });
  }

  const numerada = previa?.formato === 'NUMERADA' ? previa : undefined;
  const plana = previa?.formato === 'PLANA' ? previa : undefined;
  const porPeca = previa?.formato === 'POR_PECA' ? previa : undefined;
  const escolhidas = numerada === undefined ? 0 : numerada.include.size;

  // O desenho que recebe a planilha por peça sai do atrelamento de Pavimentos e desenhos
  // (RN-392): a coluna ANDAR o diz, e o que não fecha trava a importação, com o motivo.
  const destinoDasPecas = useMemo(
    () =>
      porPeca === undefined
        ? undefined
        : destinoPeloAtrelamento({
            linhas: porPeca.leitura.linhas,
            areas: workspace === undefined ? SEM_DESTINOS_POR_PECA : destinosPorPeca(workspace),
            desenhos: nomesDosDesenhos ?? SEM_DESENHOS,
            pavimentos: pavimentosDaObra ?? SEM_PAVIMENTOS,
          }),
    [porPeca, workspace, nomesDosDesenhos, pavimentosDaObra],
  );
  // A área do desenho: a única, ou a escolhida entre as dele.
  const destinoDaPlanilhaPorPeca =
    destinoDasPecas?.ok !== true
      ? undefined
      : destinoDasPecas.areas.length === 1
        ? destinoDasPecas.areas[0]!.unitTypeId
        : destinoDasPecas.areas.find((area) => area.unitTypeId === destinoEscolhidoDasPecas)
            ?.unitTypeId;
  /**
   * As peças que a tipologia de destino já tem e a planilha nova não traz (RN-386): elas
   * continuam, e a tela diz quais — é a diferença entre "atualizou" e "esqueci a metade".
   */
  const naoVieram = useMemo(() => {
    if (porPeca === undefined || destinoDaPlanilhaPorPeca === undefined) return [];
    const destino = workspace?.typologies.find(
      (tipologia) => tipologia.unitTypeId === destinoDaPlanilhaPorPeca,
    );
    const atual = destino?.pieceQuantities;
    // A peça renomeada (RN-458) veio, pelo nome antigo: não entra no aviso.
    return atual === undefined
      ? []
      : juntarQuantidadesPorPeca(atual, porPeca.mescla.quantidades, destino?.pieceNameAliases)
          .naoVieram;
  }, [porPeca, destinoDaPlanilhaPorPeca, workspace]);
  const pecasDaPlanilha =
    porPeca === undefined
      ? 0
      : Object.values(porPeca.mescla.resumo.pecasPorDisciplina).reduce(
          (total, pecas) => total + pecas,
          0,
        );
  const temQuantidadePorPeca =
    porPeca !== undefined &&
    (Object.keys(porPeca.mescla.quantidades.itens).length > 0 ||
      Object.keys(porPeca.mescla.quantidades.servicos).length > 0);

  // ── Completar para orçar (13/10/2026) ──
  //
  // Os produtos saem da mescla **sem** escolhas: a mão de obra que não casou pelo nome continua
  // na lista depois de alguém escolher a função, para a escolha poder ser trocada.
  const produtosParaOrcar = useMemo(
    () =>
      porPeca === undefined ? undefined : produtosDaImportacao(porPeca.mescla, funcoesDoServico),
    [porPeca, funcoesDoServico],
  );
  const paraOrcar = useCompletarParaOrcar(produtosParaOrcar, projectId);
  const passo = useMemo(
    () =>
      produtosParaOrcar === undefined
        ? undefined
        : passoParaOrcar(
            produtosParaOrcar,
            paraOrcar.estado === 'PRONTA' ? paraOrcar.consulta : undefined,
          ),
    [produtosParaOrcar, paraOrcar.estado, paraOrcar.consulta],
  );
  const pedido = useMemo(
    () => (passo === undefined ? undefined : pedidoParaOrcar(passo, escolhas)),
    [passo, escolhas],
  );
  /**
   * A mescla com as funções escolhidas — a que a prévia mostra e a confirmação despacha.
   *
   * Refeita só quando a escolha muda (a mescla da v1.3 leva de 100 a 300 ms): sem escolha, é a
   * mesma da leitura do arquivo.
   */
  const funcoesEscolhidas = escolhas.funcoes;
  const mesclaPorPeca = useMemo(
    () =>
      porPeca === undefined
        ? undefined
        : funcoesEscolhidas.size === 0
          ? porPeca.mescla
          : mesclarPorPeca(
              anterior,
              disponiveis,
              porPeca.leitura,
              desativados,
              funcoesDoServico,
              funcoesEscolhidas,
            ),
    [porPeca, funcoesEscolhidas, anterior, disponiveis, desativados, funcoesDoServico],
  );
  const ligadas = useMemo(
    () =>
      mesclaPorPeca === undefined || produtosParaOrcar === undefined
        ? new Map<string, number>()
        : ligacoesDaMaoDeObra(mesclaPorPeca.catalogo, produtosParaOrcar.maoDeObra),
    [mesclaPorPeca, produtosParaOrcar],
  );

  /**
   * As ações da planilha por peça: o catálogo primeiro, porque as quantidades apontam para os
   * itens que ele cria. Vai sempre, e não só com item novo: a mescla também traz os serviços do
   * cadastro que ninguém tinha aberto nesta tela, e sem eles no catálogo a quantidade do
   * serviço não teria como subir.
   */
  function acoesDaPorPeca(
    mescla: PreviaPorPeca['mescla'],
    unitTypeId: UnitTypeId,
  ): readonly WorkspaceAction[] {
    return [
      { type: 'IMPORT_CATALOG', catalog: mescla.catalogo },
      { type: 'IMPORT_PIECE_QUANTITIES', unitTypeId, quantidades: mescla.quantidades },
    ];
  }

  // O desenho que recebe as estacas: o escolhido, ou o único que há. Com dois ou mais e nenhuma
  // escolha, ninguém decide por quem importa: a estaca sem pavimento é avisada (F10).
  const destinos = useMemo(
    () => (workspace === undefined ? SEM_DESTINOS : destinosDeEstacas(workspace)),
    [workspace],
  );
  const destinoPadrao =
    destinos.find((destino) => destino.unitTypeId === destinoEscolhido)?.unitTypeId ??
    (destinos.length === 1 ? destinos[0]!.unitTypeId : undefined);

  // Mesclada na prévia, e é o mesmo objeto que a confirmação despacha — o que se vê é o que
  // entra, inclusive o id dos tipos novos.
  const fundacao = useMemo(
    () =>
      plana === undefined || workspace === undefined || projectId === undefined
        ? undefined
        : mesclarEstacas(plana.leitura, {
            projectId,
            pileTypes: workspace.pileTypes ?? SEM_TIPOS,
            destinos,
            ...(destinoPadrao === undefined ? {} : { destinoPadrao }),
            agora: new Date().toISOString(),
            novoId: () => asId<'PileType'>(crypto.randomUUID()),
          }),
    [plana, workspace, projectId, destinos, destinoPadrao],
  );

  const catalogoMudou =
    plana !== undefined &&
    plana.mescla.resumo.itensNovos +
      plana.mescla.resumo.itensAtualizados +
      plana.mescla.resumo.taxasGravadas >
      0;
  const mudancas =
    plana === undefined
      ? 0
      : plana.mescla.resumo.itensNovos +
        plana.mescla.resumo.itensAtualizados +
        plana.mescla.resumo.taxasGravadas +
        (fundacao?.resumo.tiposNovos ?? 0) +
        (fundacao?.resumo.tiposAtualizados ?? 0) +
        (fundacao?.resumo.estacasNovas ?? 0);

  /** As ações da confirmação: só as que mudam alguma coisa, e o catálogo antes do que o usa. */
  function acoesDaPlana(da: PreviaPlana): readonly WorkspaceAction[] {
    const acoes: WorkspaceAction[] = [];
    if (catalogoMudou) acoes.push({ type: 'IMPORT_CATALOG', catalog: da.mescla.catalogo });
    if (fundacao !== undefined) {
      if (fundacao.resumo.tiposNovos + fundacao.resumo.tiposAtualizados > 0) {
        acoes.push({ type: 'SET_PILE_TYPES', pileTypes: fundacao.pileTypes });
      }
      for (const destino of fundacao.porTipologia) {
        acoes.push({
          type: 'IMPORT_PILES',
          unitTypeId: destino.unitTypeId,
          piles: destino.piles,
        });
      }
    }
    return acoes;
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Importar planilha de serviços</DialogTitle>
        <DialogDescription>
          Escolha o arquivo, confira o que cada aba é e o que entra. Nada é gravado até confirmar.
          {anterior !== undefined && (
            <>
              {' '}
              A obra já tem catálogo: a planilha numerada o troca pelas abas escolhidas; a de
              índices e a por peça acrescentam ao que já existe.
            </>
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) p-3">
          <FileSpreadsheet className="size-5 text-(--color-content-muted)" aria-hidden="true" />
          <p className="min-w-0 flex-1 truncate text-sm">
            {previa === undefined && naoReconhecida !== undefined ? (
              <>
                <strong>{naoReconhecida.fileName}</strong>{' '}
                <span className="text-(--color-content-muted)">· colunas a escolher</span>
              </>
            ) : previa === undefined ? (
              <span className="text-(--color-content-muted)">
                {lendo
                  ? 'Lendo a planilha…'
                  : 'Nenhum arquivo escolhido. A pasta inteira é lida, aba por aba.'}
              </span>
            ) : (
              <>
                <strong>{previa.fileName}</strong>{' '}
                <span className="text-(--color-content-muted)">
                  ·{' '}
                  {previa.formato === 'PLANA'
                    ? `planilha de índices, taxas e estacas · ${String(previa.leitura.abas.length)} ${previa.leitura.abas.length === 1 ? 'aba' : 'abas'}`
                    : previa.formato === 'POR_PECA'
                      ? `planilha por peça · ${quantos(previa.leitura.abas.length, 'aba', 'abas')}`
                      : `${String(previa.sheets.length)} ${previa.sheets.length === 1 ? 'aba' : 'abas'}`}
                </span>
              </>
            )}
          </p>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.csv,.tsv,.txt"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file !== undefined) void escolher(file);
              event.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={lendo}
            onClick={() => fileInput.current?.click()}
          >
            <Upload aria-hidden="true" />
            {lendo ? 'Lendo…' : previa === undefined ? 'Escolher arquivo' : 'Trocar arquivo'}
          </Button>
        </div>

        {erro !== undefined && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-(--color-state-danger) px-3 py-2 text-sm"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            {erro}
          </p>
        )}

        {naoReconhecida !== undefined && (
          <MapearColunas
            key={naoReconhecida.fileName}
            fileName={naoReconhecida.fileName}
            abas={naoReconhecida.abas}
            onLer={(formato, abas) => lerMapeada(naoReconhecida, formato, abas)}
            onCancelar={() => {
              setNaoReconhecida(undefined);
              setErro(undefined);
            }}
          />
        )}

        {numerada !== undefined && (
          <>
            <AbasDaPasta previa={numerada} onToggle={alternar} />
            <Conferencia conference={numerada.conference} />
          </>
        )}

        {plana !== undefined && (
          <PreviaDaPlanilhaPlana
            previa={plana}
            fundacao={fundacao}
            semObra={workspace === undefined || projectId === undefined}
            destinos={destinos}
            destinoPadrao={destinoPadrao}
            onDestino={setDestinoEscolhido}
          />
        )}

        {porPeca !== undefined && mesclaPorPeca !== undefined && (
          <PreviaDaPlanilhaPorPeca
            previa={
              mesclaPorPeca === porPeca.mescla ? porPeca : { ...porPeca, mescla: mesclaPorPeca }
            }
            destino={destinoDasPecas!}
            escolhida={destinoDaPlanilhaPorPeca}
            onEscolher={setDestinoEscolhidoDasPecas}
            naoVieram={naoVieram}
          />
        )}

        {passo !== undefined && pedido !== undefined && produtosParaOrcar !== undefined && (
          <CompletarParaOrcar
            passo={passo}
            estado={paraOrcar.estado}
            aPerguntar={produtosParaOrcar.comCodigo.length + produtosParaOrcar.semCodigo.length}
            escolhas={escolhas}
            onEscolhas={setEscolhas}
            pedido={pedido}
            ligadas={ligadas}
          />
        )}
      </div>

      <DialogFooter className="flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/*
          A geração de uma tipologia por aba saiu na fatia 1 da Fase 14: a tipologia
          passou a ser registro da obra, e criá-la é ato do servidor. Dispará-la daqui
          gravaria no rascunho o que a leitura do servidor sobrescreve em seguida — não
          faria nada, e não diria que não fez.

          Ela volta na fatia do catálogo, quando a planilha também for gravada e houver
          onde pendurar o vínculo aba ↔ tipologia.
        */}
        {porPeca !== undefined ? (
          <p className="text-sm text-(--color-content-muted)">
            Nada é removido: a peça que a planilha não traz continua na tipologia, com as
            quantidades dela.
          </p>
        ) : plana === undefined ? (
          <p className="text-sm text-(--color-content-muted)">
            As tipologias são cadastradas na obra, em{' '}
            <strong className="font-medium text-(--color-content)">
              Tipologias, por pavimento
            </strong>
            .
          </p>
        ) : (
          <p className="text-sm text-(--color-content-muted)">
            Nada é removido: o item que a planilha não cita continua no serviço.
          </p>
        )}

        {naoReconhecida !== undefined ? (
          // O mapeamento tem o próprio botão ("Ler com estas colunas"); o do rodapé só importa
          // depois que a prévia existe — e diz isso, em vez de pedir uma aba que não há.
          <Button type="button" variant="primary" disabled>
            Escolha as colunas primeiro
          </Button>
        ) : porPeca !== undefined ? (
          <Button
            type="button"
            variant="primary"
            disabled={!temQuantidadePorPeca || destinoDaPlanilhaPorPeca === undefined}
            onClick={() => {
              if (destinoDaPlanilhaPorPeca === undefined) return;
              // A importação primeiro, e inteira: o fator e o provisório são outra gravação, no
              // servidor, e a falha dela não desfaz o catálogo — vira aviso.
              onConfirmar(
                acoesDaPorPeca(mesclaPorPeca ?? porPeca.mescla, destinoDaPlanilhaPorPeca),
              );
              if (pedido !== undefined) void paraOrcar.gravar(pedido);
            }}
          >
            {!temQuantidadePorPeca
              ? 'Nada a importar'
              : destinoDasPecas?.ok === false
                ? 'Importação travada'
                : destinoDaPlanilhaPorPeca === undefined
                  ? 'Escolha a área que recebe'
                  : `Importar ${quantos(pecasDaPlanilha, 'peça', 'peças')}`}
          </Button>
        ) : plana === undefined ? (
          <Button
            type="button"
            variant="primary"
            disabled={numerada === undefined || escolhidas === 0}
            onClick={() => {
              if (numerada === undefined) return;
              const catalogo = buildCatalog({
                fileName: numerada.fileName,
                now: new Date().toISOString(),
                sheets: numerada.sheets,
                include: numerada.include,
                ...(anterior === undefined ? {} : { previous: anterior }),
              });
              // A troca é do retrato inteiro (RN-255): o item que o catálogo novo não tem sai da
              // obra. Quando sai alguma coisa, quem decide é a pessoa, vendo quanto.
              const removidos = removidosPelaTroca(anterior, catalogo);
              if (removidos.servicos.length > 0) {
                setTrocaPendente({ catalogo, removidos });
                return;
              }
              onConfirmar([{ type: 'IMPORT_CATALOG', catalog: catalogo }]);
            }}
          >
            {escolhidas === 0
              ? 'Escolha ao menos uma aba'
              : `Importar ${String(escolhidas)} ${escolhidas === 1 ? 'aba' : 'abas'}`}
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            disabled={mudancas === 0}
            onClick={() => onConfirmar(acoesDaPlana(plana))}
          >
            {mudancas === 0
              ? 'Nada a aplicar'
              : `Aplicar ${String(mudancas)} ${mudancas === 1 ? 'mudança' : 'mudanças'}`}
          </Button>
        )}
      </DialogFooter>

      <ConfirmDialog
        open={trocaPendente !== undefined}
        onOpenChange={(aberta) => {
          if (!aberta) setTrocaPendente(undefined);
        }}
        destructive
        title="Esta planilha apaga itens da obra"
        description={descricaoDaTroca(trocaPendente?.removidos)}
        confirmLabel={
          trocaPendente === undefined || trocaPendente.removidos.items === 0
            ? 'Trocar a planilha'
            : `Trocar e apagar ${quantos(trocaPendente.removidos.items, 'item', 'itens')}`
        }
        onConfirm={() => {
          const pendente = trocaPendente;
          setTrocaPendente(undefined);
          if (pendente !== undefined) {
            onConfirmar([{ type: 'IMPORT_CATALOG', catalog: pendente.catalogo }]);
          }
        }}
      >
        {trocaPendente !== undefined && (
          <ul className="mt-3 max-h-48 list-disc overflow-y-auto pl-5 text-sm">
            {trocaPendente.removidos.servicos.slice(0, SERVICOS_VISIVEIS).map((servico) => (
              <li key={`${servico.disciplineName}|${servico.serviceName}`}>
                {servico.disciplineName} › {servico.serviceName} —{' '}
                {servico.removido
                  ? `o serviço sai${servico.items > 0 ? ` com ${quantos(servico.items, 'item', 'itens')}` : ''}`
                  : quantos(servico.items, 'item', 'itens')}
              </li>
            ))}
            {trocaPendente.removidos.servicos.length > SERVICOS_VISIVEIS && (
              <li>
                e mais{' '}
                {quantos(
                  trocaPendente.removidos.servicos.length - SERVICOS_VISIVEIS,
                  'serviço',
                  'serviços',
                )}
              </li>
            )}
          </ul>
        )}
      </ConfirmDialog>
    </>
  );
}

/** Quantos serviços a confirmação da troca lista: o resto é dito em uma linha. */
const SERVICOS_VISIVEIS = 8;

function descricaoDaTroca(removidos: RemovidosDaTroca | undefined): string {
  if (removidos === undefined) return '';
  const partes = [
    removidos.items > 0
      ? `${quantos(removidos.items, 'item que a obra tem hoje não está', 'itens que a obra tem hoje não estão')} nesta planilha e ${removidos.items === 1 ? 'sai' : 'saem'}, com os índices e as perdas`
      : '',
    removidos.servicosRemovidos > 0
      ? `${quantos(removidos.servicosRemovidos, 'serviço sai', 'serviços saem')} do catálogo`
      : '',
  ].filter((parte) => parte !== '');
  return `A planilha numerada monta o catálogo só com as abas escolhidas: ${partes.join('; ')}. Itens vindos da planilha de índices ou acrescentados na tela entram nessa conta. Não há como desfazer.`;
}

// ── A planilha plana ────────────────────────────────────────────────────────────

const ROTULO_DA_ABA: Readonly<Record<TipoDeAba, string>> = {
  INDICES: 'índices',
  TAXAS: 'taxas de aço',
  TIPOS: 'tipos de estaca',
  ESTACAS: 'estacas',
  IGNORADA: 'ignorada',
};

const TOM_DA_ABA: Readonly<Record<TipoDeAba, 'ok' | 'info' | 'neutral'>> = {
  INDICES: 'ok',
  TAXAS: 'info',
  TIPOS: 'info',
  ESTACAS: 'info',
  IGNORADA: 'neutral',
};

/** Quantos avisos a tabela mostra: o resto é dito em uma linha, como na conferência. */
const AVISOS_VISIVEIS = 50;

function quantos(n: number, um: string, muitos: string): string {
  return `${String(n)} ${n === 1 ? um : muitos}`;
}

/**
 * O que a planilha plana vai fazer na obra, antes de ela fazer.
 *
 * A pergunta é a mesma da planilha numerada — o que entra, o que muda, o que não casou —, e a
 * resposta vem em números e numa tabela de avisos. O aviso é o centro: é nele que o usuário vê
 * que o aço da MONTAGEM não entrou (RN-366) e que o serviço escrito torto não casou.
 */
function PreviaDaPlanilhaPlana({
  previa,
  fundacao,
  semObra,
  destinos,
  destinoPadrao,
  onDestino,
}: {
  previa: PreviaPlana;
  fundacao: MesclaDasEstacas | undefined;
  /** A tela não recebeu a obra: tipos e estacas, se a planilha os traz, não são lidos. */
  semObra: boolean;
  destinos: readonly DestinoDeEstacas[];
  destinoPadrao: UnitTypeId | undefined;
  onDestino: (unitTypeId: UnitTypeId | undefined) => void;
}) {
  const { resumo } = previa.mescla;
  const outras = resumo.itensAtualizados - resumo.indicesAtualizados;
  // Só a aba que traz linha conta: o modelo exporta Tipos e Estacas mesmo numa obra sem nenhum, e
  // perguntar o pavimento de uma aba só com o cabeçalho seria pergunta sem resposta.
  const abasDaFundacao = previa.leitura.abas.filter(
    (aba) => (aba.tipo === 'TIPOS' || aba.tipo === 'ESTACAS') && aba.linhas > 0,
  );
  const temTipos = abasDaFundacao.some((aba) => aba.tipo === 'TIPOS');
  const temEstacas = abasDaFundacao.some((aba) => aba.tipo === 'ESTACAS');
  const daFundacao = fundacao?.resumo;

  // Uma tabela só, na ordem da pasta: os avisos do catálogo e os das estacas se misturam.
  const avisos: readonly AvisoDaPlanilha[] = ordenarAvisos(previa.leitura.abas, [
    ...resumo.avisos,
    ...(daFundacao?.avisos ?? []),
    ...(semObra
      ? abasDaFundacao.map((aba) => ({
          aba: aba.name,
          mensagem: `A aba "${aba.name}" não foi lida: a tela não recebeu a obra aberta.`,
        }))
      : []),
  ]);

  return (
    <>
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">O que a planilha traz</h3>
        <ul className="flex flex-wrap gap-2">
          <li>
            <Badge tone="ok">{quantos(resumo.itensNovos, 'item novo', 'itens novos')}</Badge>
          </li>
          <li>
            <Badge tone="ok">
              {quantos(resumo.indicesAtualizados, 'índice atualizado', 'índices atualizados')}
            </Badge>
          </li>
          {outras > 0 && (
            <li>
              <Badge tone="info">
                {quantos(outras, 'item com outra mudança', 'itens com outra mudança')}
              </Badge>
            </li>
          )}
          <li>
            <Badge tone={resumo.taxasGravadas > 0 ? 'ok' : 'neutral'}>
              {quantos(resumo.taxasGravadas, 'taxa de aço', 'taxas de aço')}
            </Badge>
          </li>
          {resumo.itensComBitola > 0 && (
            <li>
              <Badge tone="info">
                {quantos(
                  resumo.itensComBitola,
                  'item de aço com bitola',
                  'itens de aço com bitola',
                )}
              </Badge>
            </li>
          )}
          {daFundacao !== undefined && temTipos && (
            <>
              <li>
                <Badge tone={daFundacao.tiposNovos > 0 ? 'ok' : 'neutral'}>
                  {quantos(daFundacao.tiposNovos, 'tipo de estaca novo', 'tipos de estaca novos')}
                </Badge>
              </li>
              <li>
                <Badge tone={daFundacao.tiposAtualizados > 0 ? 'ok' : 'neutral'}>
                  {quantos(
                    daFundacao.tiposAtualizados,
                    'tipo de estaca atualizado',
                    'tipos de estaca atualizados',
                  )}
                </Badge>
              </li>
            </>
          )}
          {daFundacao !== undefined && temEstacas && (
            <li>
              <Badge tone={daFundacao.estacasNovas > 0 ? 'ok' : 'neutral'}>
                {quantos(daFundacao.estacasNovas, 'estaca nova', 'estacas novas')} a posicionar
              </Badge>
            </li>
          )}
          {resumo.naoCasados.length > 0 && (
            <li>
              <Badge tone="warn" icon={<AlertTriangle className="size-3" aria-hidden="true" />}>
                {resumo.naoCasados.length} não{' '}
                {resumo.naoCasados.length === 1 ? 'casou' : 'casaram'}
              </Badge>
            </li>
          )}
        </ul>
        <ul className="flex flex-col gap-1">
          {previa.leitura.abas.map((aba) => (
            <li key={aba.name} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium">{aba.name}</span>
              <Badge tone={TOM_DA_ABA[aba.tipo]}>{ROTULO_DA_ABA[aba.tipo]}</Badge>
              {aba.tipo !== 'IGNORADA' && (
                <span className="text-(--color-content-muted)">
                  {quantos(aba.linhas, 'linha lida', 'linhas lidas')}
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>

      {temEstacas && !semObra && (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">Estacas da planilha</h3>
          {destinos.length === 0 ? (
            <p className="text-xs text-(--color-content-muted)">
              A obra não tem pavimento de Fundação ou Contenção com área comum. Use{' '}
              <strong className="font-medium text-(--color-content)">
                Desenhar este pavimento
              </strong>{' '}
              primeiro: as estacas não entram agora, e os tipos entram do mesmo jeito.
            </p>
          ) : (
            <>
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">Pavimento das estacas</span>
                <select
                  className={SELECT_CLASS}
                  value={destinoPadrao ?? ''}
                  onChange={(event) =>
                    onDestino(
                      event.target.value === '' ? undefined : (event.target.value as UnitTypeId),
                    )
                  }
                >
                  {destinoPadrao === undefined && <option value="">Escolha o pavimento</option>}
                  {destinos.map((destino) => (
                    <option key={destino.unitTypeId} value={destino.unitTypeId}>
                      {destino.nome} · {destino.pavimentos.join(', ')}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-xs text-(--color-content-muted)">
                A coluna Pavimento da planilha, quando preenchida, vale no lugar desta escolha,
                linha a linha. As estacas chegam sem posição: entram na fila do posicionamento e só
                contam depois de clicadas na planta.
              </p>
            </>
          )}
        </section>
      )}

      <TabelaDeAvisos avisos={avisos} />
    </>
  );
}

/** Os avisos da planilha, na ordem da pasta — a mesma tabela na plana e na por peça. */
function TabelaDeAvisos({ avisos }: { avisos: readonly AvisoDaPlanilha[] }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">
        {avisos.length === 0 ? 'Nenhum aviso' : `Avisos (${String(avisos.length)})`}
      </h3>
      {avisos.length === 0 ? (
        <p className="text-xs text-(--color-content-muted)">
          Tudo o que a planilha traz casou com os serviços da obra.
        </p>
      ) : (
        <>
          <TableWrapper className="max-h-56 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Aba</TableHead>
                  <TableHead className="text-right">Linha</TableHead>
                  <TableHead>Aviso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {avisos.slice(0, AVISOS_VISIVEIS).map((aviso, indice) => (
                  <TableRow key={`${aviso.aba}|${String(aviso.linha)}|${String(indice)}`}>
                    <TableCell className="text-xs text-(--color-content-muted)">
                      {aviso.aba}
                    </TableCell>
                    <TableCell className="tabular text-right">{aviso.linha ?? '—'}</TableCell>
                    <TableCell className="text-sm">{aviso.mensagem}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
          {avisos.length > AVISOS_VISIVEIS && (
            <p className="text-xs text-(--color-content-muted)">
              Mostrando os {AVISOS_VISIVEIS} primeiros de {avisos.length}.
            </p>
          )}
        </>
      )}
    </section>
  );
}

// ── A planilha por peça ─────────────────────────────────────────────────────────

/** Quantas peças que não vieram a linha cita pelo nome: o resto é dito em número. */
const PECAS_CITADAS = 12;

/**
 * O que a planilha por peça vai fazer na obra, antes de ela fazer (Fase 28).
 *
 * As mesmas perguntas da plana — o que entra, o que casou, o que não casou —, e duas a mais:
 * **para onde** vai (a área única da Fundação ou da Contenção, RN-392, ou a área comum de um
 * pavimento) e **o que fica** de
 * antes (as peças que a tipologia já tinha e esta planilha não traz, RN-386). O de→para dos
 * nomes vem à vista porque é a parte que pode surpreender: "BLOCOS DE COROAMENTO" virou o
 * BLOCO do catálogo, e quem importa precisa ver isso antes de confirmar.
 */
function PreviaDaPlanilhaPorPeca({
  previa,
  destino,
  escolhida,
  onEscolher,
  naoVieram,
}: {
  previa: PreviaPorPeca;
  /** O desenho pelo atrelamento, ou o motivo da trava (RN-392). */
  destino: DestinoDaPlanilha;
  /** A área que recebe: a única do desenho, ou a escolhida entre as dele. */
  escolhida: UnitTypeId | undefined;
  onEscolher: (unitTypeId: UnitTypeId | undefined) => void;
  naoVieram: readonly string[];
}) {
  const { resumo } = previa.mescla;
  // A leitura avisa o que não leu, e a mescla o que não casou; a plana devolve os da leitura
  // dentro do resumo. Juntos e sem repetir, a tabela vale nas duas formas.
  const vistos = new Set<string>();
  const avisos = ordenarAvisos(
    previa.leitura.abas,
    [...previa.leitura.avisos, ...resumo.avisos].filter((aviso) => {
      const chave = `${aviso.aba}|${String(aviso.linha)}|${aviso.mensagem}`;
      if (vistos.has(chave)) return false;
      vistos.add(chave);
      return true;
    }),
  );
  const casados = [...resumo.disciplinasCasadas, ...resumo.servicosCasados];
  const nomeDoDestino = destino.ok
    ? destino.areas.find((area) => area.unitTypeId === escolhida)?.rotulo
    : undefined;

  return (
    <>
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">O que a planilha por peça traz</h3>
        <ul className="flex flex-wrap gap-2">
          {Object.entries(resumo.pecasPorDisciplina).map(([disciplina, pecas]) => (
            <li key={disciplina}>
              <Badge tone="ok">
                {quantos(pecas, 'peça', 'peças')} em {disciplina}
              </Badge>
            </li>
          ))}
          <li>
            <Badge tone={resumo.linhasAproveitadas > 0 ? 'ok' : 'neutral'}>
              {quantos(resumo.linhasAproveitadas, 'linha aproveitada', 'linhas aproveitadas')}
            </Badge>
          </li>
          <li>
            <Badge tone={resumo.itensNovos > 0 ? 'ok' : 'neutral'}>
              {quantos(resumo.itensNovos, 'item novo', 'itens novos')}
            </Badge>
          </li>
          {resumo.funcoesLigadas > 0 && (
            // A mão de obra ligada à função da folha pelo nome: o R$/h vem dela (Fase 29, D11).
            <li>
              <Badge tone="ok">
                {quantos(
                  resumo.funcoesLigadas,
                  'item de mão de obra ligado à função',
                  'itens de mão de obra ligados à função',
                )}
              </Badge>
            </li>
          )}
          {resumo.codigosNovos > 0 && (
            // O código de compras que a planilha trouxe é o que dá preço ao material (Fase 29).
            <li>
              <Badge tone="ok">
                {quantos(
                  resumo.codigosNovos,
                  'item ganha código de compras',
                  'itens ganham código de compras',
                )}
              </Badge>
            </li>
          )}
          {resumo.naoCasados.length > 0 && (
            <li>
              <Badge tone="warn" icon={<AlertTriangle className="size-3" aria-hidden="true" />}>
                {resumo.naoCasados.length} não{' '}
                {resumo.naoCasados.length === 1 ? 'casou' : 'casaram'}
              </Badge>
            </li>
          )}
        </ul>
        <ul className="flex flex-col gap-1">
          {previa.leitura.abas.map((aba) => (
            <li key={aba.name} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-medium">{aba.name}</span>
              <span className="text-(--color-content-muted)">
                {quantos(aba.linhas, 'linha lida', 'linhas lidas')}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-(--color-content-muted)">
          A quantidade de cada peça entra pronta: não vira índice, e o desenho não a recalcula. Ela
          conta já, antes de a peça ser posicionada na planta.
        </p>
      </section>

      {casados.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">Nomes casados com o catálogo</h3>
          <ul className="flex flex-col gap-1 text-sm" aria-label="Nomes casados com o catálogo">
            {casados.map((nome) => (
              <li key={`${nome.daPlanilha}|${nome.doCatalogo}`}>
                <span className="text-(--color-content-muted)">{nome.daPlanilha}</span> →{' '}
                <span className="font-medium">{nome.doCatalogo}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        Cada nome que ficou de fora vem com as peças que ele levou (22/10/2026): "1 não casou"
        não dizia quais peças perderam o serviço, e é a peça que se procura na planilha.
      */}
      {resumo.foraDaImportacao.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">O que não casou e não entra</h3>
          <p className="text-xs text-(--color-content-muted)">
            Estas linhas ficam de fora; as peças entram sem elas. Cadastre o serviço na Estrutura,
            ou corrija o nome na planilha, e importe de novo.
          </p>
          <ul aria-label="Nomes que não entram" className="flex flex-col gap-2">
            {resumo.foraDaImportacao.map((fora) => (
              <li
                key={fora.rotulo}
                className="flex flex-col gap-1 rounded-md border border-(--color-state-warn) px-3 py-2"
              >
                <p className="text-sm">
                  <span className="font-medium">{fora.rotulo}</span>
                  <span className="text-xs text-(--color-content-muted)">
                    {' · '}
                    {quantos(fora.linhas, 'linha', 'linhas')} ·{' '}
                    {quantos(fora.pecas.length, 'peça', 'peças')}
                  </span>
                </p>
                <p className="text-xs text-(--color-content-muted)">{fora.mensagem}</p>
                <p
                  aria-label={`Peças sem ${fora.rotulo}`}
                  className="font-condensed max-h-32 overflow-y-auto text-xs leading-relaxed"
                >
                  {fora.pecas.join(', ')}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Para onde vão as peças</h3>
        {!destino.ok ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-(--color-state-warn) px-3 py-2 text-sm"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>{destino.motivo}</span>
          </p>
        ) : (
          <>
            <p className="text-sm">
              Desenho <strong className="font-medium">{destino.desenho}</strong>, pela coluna ANDAR
              ("{destino.andar}"). Em Pavimentos e desenhos ele está atrelado a{' '}
              {destino.pavimentos.length === 1
                ? `1 pavimento (${destino.pavimentosCitados}): cada quantidade da planilha conta uma vez.`
                : `${String(destino.pavimentos.length)} pavimentos (${destino.pavimentosCitados}): cada quantidade da planilha conta ${String(destino.pavimentos.length)} vezes no orçamento.`}
            </p>
            {destino.areas.length === 1 ? (
              <p className="text-sm">
                Recebe: <strong className="font-medium">{destino.areas[0]!.rotulo}</strong>
              </p>
            ) : (
              <label className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">Área que recebe</span>
                <select
                  className={SELECT_CLASS}
                  value={escolhida ?? ''}
                  onChange={(event) =>
                    onEscolher(
                      event.target.value === '' ? undefined : (event.target.value as UnitTypeId),
                    )
                  }
                >
                  {escolhida === undefined && (
                    <option value="" disabled>
                      Escolha a área do desenho {destino.desenho}
                    </option>
                  )}
                  {destino.areas.map((area) => (
                    <option key={area.unitTypeId} value={area.unitTypeId}>
                      {area.rotulo}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <p className="text-xs text-(--color-content-muted)">
              Reimportar atualiza as peças que vierem — todas as quantidades delas —, e a que não
              vier continua como estava.
            </p>
            {naoVieram.length > 0 && (
              <p
                role="status"
                className="flex items-start gap-2 rounded-md border border-(--color-state-warn) px-3 py-2 text-sm"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>
                  {quantos(naoVieram.length, 'peça que já está em', 'peças que já estão em')}{' '}
                  {nomeDoDestino ?? 'esta tipologia'} não{' '}
                  {naoVieram.length === 1 ? 'veio' : 'vieram'} nesta planilha e{' '}
                  {naoVieram.length === 1 ? 'continua' : 'continuam'}:{' '}
                  {naoVieram.slice(0, PECAS_CITADAS).join(', ')}
                  {naoVieram.length > PECAS_CITADAS &&
                    ` e mais ${String(naoVieram.length - PECAS_CITADAS)}`}
                  .
                </span>
              </p>
            )}
          </>
        )}
      </section>

      <TabelaDeAvisos avisos={avisos} />
    </>
  );
}

// ── As abas ─────────────────────────────────────────────────────────────────────

function AbasDaPasta({
  previa,
  onToggle,
}: {
  previa: PreviaNumerada;
  onToggle: (name: string) => void;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">O que há em cada aba</h3>
      <ul className="flex flex-col gap-2">
        {previa.sheets.map((aba) => {
          const vazia = aba.scope === 'VAZIA';
          return (
            <li
              key={aba.sheet.name}
              className="flex flex-col gap-1 rounded-md border border-(--color-border-subtle) px-3 py-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={previa.include.has(aba.sheet.name)}
                    disabled={vazia}
                    onChange={() => onToggle(aba.sheet.name)}
                  />
                  {aba.sheet.name}
                </label>
                <Badge tone={TOM_POR_ESCOPO[aba.scope]}>{SHEET_SCOPE_LABEL[aba.scope]}</Badge>
                <span className="text-xs text-(--color-content-muted)">
                  {vazia ? 'sem coluna de item reconhecível' : describeSheet(aba.summary)}
                </span>
              </div>

              {aba.scope === 'SOMA' && (
                <p className="text-xs text-(--color-content-muted)">
                  Fica de fora por padrão: ela já é a soma das outras, e importá-la junto contaria o
                  prédio duas vezes. Continua servindo de conferência.
                </p>
              )}

              {aba.scope === 'PARCELA' && aba.foreignServices.length > 0 && (
                <p className="text-xs text-(--color-content-muted)">
                  É parcela da soma, mas traz {aba.foreignServices.length}{' '}
                  {aba.foreignServices.length === 1 ? 'serviço' : 'serviços'} que{' '}
                  <strong className="font-medium">a aba de soma não lista</strong> — vale conferir
                  se ela está incompleta:{' '}
                  <span className="italic">{aba.foreignServices.slice(0, 3).join('; ')}</span>
                  {aba.foreignServices.length > 3 &&
                    ` e mais ${String(aba.foreignServices.length - 3)}`}
                  .
                </p>
              )}

              {aba.scope === 'PROPRIO' && (
                <p className="text-xs text-(--color-content-muted)">
                  Traz {aba.foreignServices.length}{' '}
                  {aba.foreignServices.length === 1 ? 'serviço' : 'serviços'} que a soma não tem,
                  então não é parcela dela:{' '}
                  <span className="italic">{aba.foreignServices.slice(0, 3).join('; ')}</span>
                  {aba.foreignServices.length > 3 &&
                    ` e mais ${String(aba.foreignServices.length - 3)}`}
                  .
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ── A conferência ───────────────────────────────────────────────────────────────

function Conferencia({ conference }: { conference: SheetConference }) {
  if (conference.totalSheetName === undefined) {
    return (
      <p className="text-sm text-(--color-content-muted)">
        Nenhuma aba de soma — nada a conferir. A conferência compara a soma das parcelas com uma aba
        chamada "Andar Tipo" ou "Pavimento Tipo".
      </p>
    );
  }

  const bate = conference.divergences.length === 0;

  return (
    <section className="flex flex-col gap-2">
      <h3 className="flex flex-wrap items-center gap-2 text-sm font-medium">
        Conferência contra “{conference.totalSheetName}”
        {bate ? (
          <Badge tone="ok" icon={<Check className="size-3" aria-hidden="true" />}>
            {conference.matching} de {conference.checked} serviços batem
          </Badge>
        ) : (
          <Badge tone="neutral">
            {conference.divergences.length} de {conference.checked} onde a aba de soma discorda
          </Badge>
        )}
      </h3>

      {bate ? (
        <p className="text-xs text-(--color-content-muted)">
          A soma das parcelas dá a aba de soma, serviço a serviço: nenhuma aba ficou de fora,
          nenhuma entrou duas vezes e as colunas foram lidas na posição certa.
        </p>
      ) : (
        <>
          <p className="text-xs text-(--color-content-muted)">
            <strong className="font-medium text-(--color-content)">
              O que o sistema adota é a coluna “Parcelas”.
            </strong>{' '}
            A aba de soma é uma conta que alguém fez na planilha, e é ela que costuma envelhecer — o
            sistema refaz a soma a partir das tipologias, que são a fonte. A lista abaixo é,
            portanto, um retrato do arquivo, e não um impedimento para importar.
          </p>
          <p className="text-xs text-(--color-content-muted)">
            Vale um olhar mesmo assim, por um motivo diferente: divergência espalhada por{' '}
            <em>todos</em> os serviços costuma ser leitura torta — coluna deslocada, aba de fora —,
            enquanto diferença em alguns é a soma da planilha desatualizada.
          </p>
          <TableWrapper className="max-h-56 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Serviço</TableHead>
                  <TableHead className="text-right">
                    Parcelas
                    <span className="block text-xs font-normal text-(--color-content-muted)">
                      o que vale
                    </span>
                  </TableHead>
                  <TableHead className="text-right">
                    Aba de soma
                    <span className="block text-xs font-normal text-(--color-content-muted)">
                      o que o arquivo diz
                    </span>
                  </TableHead>
                  <TableHead className="text-right">Diferença</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conference.divergences.slice(0, 50).map((linha) => (
                  <TableRow key={`${linha.disciplineName}|${linha.serviceName}`}>
                    <TableCell>
                      <span className="block max-w-80 truncate font-medium">
                        {linha.serviceName}
                      </span>
                      <span className="text-xs text-(--color-content-muted)">
                        {linha.disciplineName}
                        {linha.unit === undefined ? '' : ` · ${linha.unit}`}
                      </span>
                    </TableCell>
                    <TableCell className="tabular text-right">{numero(linha.parts)}</TableCell>
                    <TableCell className="tabular text-right">{numero(linha.total)}</TableCell>
                    <TableCell className="tabular text-right font-medium">
                      {linha.difference > 0 ? '+' : ''}
                      {numero(linha.difference)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
          {conference.divergences.length > 50 && (
            <p className="text-xs text-(--color-content-muted)">
              Mostrando as 50 primeiras de {conference.divergences.length}.
            </p>
          )}
        </>
      )}
    </section>
  );
}
